/**
 * Fusión PET sobre CT/MR: detección de serie, corte nearest (IPP) y paint overlay.
 */
import {
  nearestSliceByNormal,
  planeNormalFromIop,
  readFileIop,
  readFileIpp,
} from './seriesLink'
import { getPetClutTable, mapValueToRgb } from './petClut'
import { parsePetSuvContext, pixelToSuv } from './petSuv'

export function fileModality(file) {
  if (!file) return ''
  const fromSeries =
    (file.series && (file.series.modality || file.series.Modality)) ||
    file.modality ||
    file.Modality
  if (fromSeries) return String(fromSeries).toUpperCase()
  const img = file.image
  if (img && img.data && typeof img.data.string === 'function') {
    try {
      const m = img.data.string('x00080060')
      if (m) return String(m).toUpperCase()
    } catch (e) {
      /* ignore */
    }
  }
  const desc = String(
    (file.series && file.series.seriesDescription) || file.seriesDescription || ''
  ).toUpperCase()
  if (/\bPT\b|\bPET\b/.test(desc)) return 'PT'
  if (/\bCT\b/.test(desc)) return 'CT'
  if (/\bMR\b|\bMRI\b/.test(desc)) return 'MR'
  return ''
}

export function isPetModality(mod) {
  const m = String(mod || '').toUpperCase()
  return m === 'PT' || m === 'PET' || m === 'NM'
}

export function isAnatomyModality(mod) {
  const m = String(mod || '').toUpperCase()
  return m === 'CT' || m === 'MR' || m === 'MRI'
}

export function seriesLooksPet(files) {
  if (!files || !files.length) return false
  const sample = files.find(Boolean)
  return isPetModality(fileModality(sample))
}

export function seriesLooksAnatomy(files) {
  if (!files || !files.length) return false
  return isAnatomyModality(fileModality(files.find(Boolean)))
}

/**
 * @param {{ seriesList?: Map, seriesKeys?: any[] }|null} seriesStore
 * @returns {Array<{ key: *, files: object[], modality: string, label: string }>}
 */
export function listPetSeriesFromStore(seriesStore) {
  if (!seriesStore || !seriesStore.seriesList) return []
  const keys = Array.isArray(seriesStore.seriesKeys)
    ? seriesStore.seriesKeys
    : [...seriesStore.seriesList.keys()]
  const out = []
  keys.forEach((key) => {
    const files = seriesStore.seriesList.get(key)
    if (!seriesLooksPet(files)) return
    const f = files.find(Boolean)
    const desc =
      (f && f.series && f.series.seriesDescription) ||
      (f && f.seriesDescription) ||
      'PET'
    out.push({
      key,
      files,
      modality: fileModality(f),
      label: `${desc} (${files.length})`,
    })
  })
  return out
}

export function pickPetSeriesNotInViewport(seriesStore, viewportFiles) {
  const pets = listPetSeriesFromStore(seriesStore)
  if (!pets.length) return null
  const uid =
    viewportFiles &&
    viewportFiles[0] &&
    ((viewportFiles[0].series && viewportFiles[0].series.seriesInstanceUID) ||
      viewportFiles[0].seriesInstanceUID)
  const other = uid
    ? pets.find((p) => {
        const f = p.files[0]
        const pUid =
          (f && f.series && f.series.seriesInstanceUID) || (f && f.seriesInstanceUID)
        return pUid !== uid
      })
    : null
  return other || pets[0]
}

export function nearestPetSliceIndex(anatomyFile, petFiles) {
  if (!anatomyFile || !petFiles || !petFiles.length) return -1
  const iop = readFileIop(anatomyFile) || readFileIop(petFiles[0])
  const n = planeNormalFromIop(iop)
  const ipp = readFileIpp(anatomyFile)
  if (n && ipp) {
    const target = ipp[0] * n[0] + ipp[1] * n[1] + ipp[2] * n[2]
    const idx = nearestSliceByNormal(petFiles, target, n)
    if (idx >= 0) return idx
  }
  return Math.min(
    petFiles.length - 1,
    Math.max(0, Math.round(((anatomyFile.instanceNumber || 1) - 1) * petFiles.length / 100))
  )
}

function spacingOf(file, image) {
  const sp = file && file.pixelSpacing
  if (sp && sp.length >= 2) return { row: Number(sp[0]) || 1, col: Number(sp[1]) || 1 }
  const img = image || (file && file.image)
  if (img) {
    return {
      row: Number(img.rowPixelSpacing) || 1,
      col: Number(img.columnPixelSpacing) || 1,
    }
  }
  return { row: 1, col: 1 }
}

/**
 * Esquinas PET (px) → coordenadas píxel en la imagen anatómica.
 */
export function petCornersInAnatomyPixels(petFile, petImage, anatomyFile, anatomyImage) {
  const petIpp = readFileIpp(petFile)
  const petIop = readFileIop(petFile)
  const ctIpp = readFileIpp(anatomyFile)
  const ctIop = readFileIop(anatomyFile)
  if (!petIpp || !petIop || !ctIpp || !ctIop) return null
  const petSp = spacingOf(petFile, petImage)
  const ctSp = spacingOf(anatomyFile, anatomyImage)
  const cols = (petImage && petImage.columns) || petFile.columns || 0
  const rows = (petImage && petImage.rows) || petFile.rows || 0
  if (!(cols > 0 && rows > 0)) return null

  const petToPatient = (i, j) => [
    petIpp[0] + i * petIop[0] * petSp.col + j * petIop[3] * petSp.row,
    petIpp[1] + i * petIop[1] * petSp.col + j * petIop[4] * petSp.row,
    petIpp[2] + i * petIop[2] * petSp.col + j * petIop[5] * petSp.row,
  ]
  const patientToCt = (p) => {
    const dx = p[0] - ctIpp[0]
    const dy = p[1] - ctIpp[1]
    const dz = p[2] - ctIpp[2]
    const i =
      (dx * ctIop[0] + dy * ctIop[1] + dz * ctIop[2]) / (ctSp.col || 1)
    const j =
      (dx * ctIop[3] + dy * ctIop[4] + dz * ctIop[5]) / (ctSp.row || 1)
    return { x: i, y: j }
  }
  return {
    origin: patientToCt(petToPatient(0, 0)),
    x: patientToCt(petToPatient(cols, 0)),
    y: patientToCt(petToPatient(0, rows)),
    cols,
    rows,
  }
}

export function anatomyPixelToPetPixel(ax, ay, anatomyFile, anatomyImage, petFile, petImage) {
  const petIpp = readFileIpp(petFile)
  const petIop = readFileIop(petFile)
  const ctIpp = readFileIpp(anatomyFile)
  const ctIop = readFileIop(anatomyFile)
  if (!petIpp || !petIop || !ctIpp || !ctIop) return null
  const petSp = spacingOf(petFile, petImage)
  const ctSp = spacingOf(anatomyFile, anatomyImage)
  const p = [
    ctIpp[0] + ax * ctIop[0] * ctSp.col + ay * ctIop[3] * ctSp.row,
    ctIpp[1] + ax * ctIop[1] * ctSp.col + ay * ctIop[4] * ctSp.row,
    ctIpp[2] + ax * ctIop[2] * ctSp.col + ay * ctIop[5] * ctSp.row,
  ]
  const dx = p[0] - petIpp[0]
  const dy = p[1] - petIpp[1]
  const dz = p[2] - petIpp[2]
  return {
    x: (dx * petIop[0] + dy * petIop[1] + dz * petIop[2]) / (petSp.col || 1),
    y: (dx * petIop[3] + dy * petIop[4] + dz * petIop[5]) / (petSp.row || 1),
  }
}

export function colorizePetImageData(pixelData, rows, cols, image, clutId, windowMax) {
  const table = getPetClutTable(clutId)
  const ctx = parsePetSuvContext(image)
  const vmax = windowMax > 0 ? windowMax : 5
  const out = typeof ImageData !== 'undefined' ? new ImageData(cols, rows) : null
  if (!out) return null
  const buf = out.data
  for (let i = 0; i < rows * cols; i++) {
    const suv = pixelToSuv(pixelData[i], image, ctx)
    const rgb = mapValueToRgb(suv, 0, vmax, table)
    const o = i * 4
    if (!rgb) {
      buf[o + 3] = 0
      continue
    }
    buf[o] = rgb[0]
    buf[o + 1] = rgb[1]
    buf[o + 2] = rgb[2]
    buf[o + 3] = Math.round(Math.min(255, 40 + rgb[3] * 215))
  }
  return out
}

export function sampleRoiOnPet(handles, petImage, petFile, anatomyFile, anatomyImage) {
  if (!handles || !petImage || typeof petImage.getPixelData !== 'function') return []
  const start = handles.start || handles[0]
  const end = handles.end || handles[1]
  if (!start || !end) return []
  const x0 = Math.min(start.x, end.x)
  const x1 = Math.max(start.x, end.x)
  const y0 = Math.min(start.y, end.y)
  const y1 = Math.max(start.y, end.y)
  const pd = petImage.getPixelData()
  const cols = petImage.columns
  const rows = petImage.rows
  const samples = []
  const stepX = Math.max(1, Math.floor((x1 - x0) / 24))
  const stepY = Math.max(1, Math.floor((y1 - y0) / 24))
  for (let y = y0; y <= y1; y += stepY) {
    for (let x = x0; x <= x1; x += stepX) {
      const pet = anatomyPixelToPetPixel(x, y, anatomyFile, anatomyImage, petFile, petImage)
      if (!pet) continue
      const px = Math.round(pet.x)
      const py = Math.round(pet.y)
      if (px < 0 || py < 0 || px >= cols || py >= rows) continue
      samples.push(pd[px + py * cols])
    }
  }
  return samples
}
